import { forwardRef } from "react";
import "./Button.scss";

export type ButtonSize = "xsmall" | "small" | "default" | "large" | "xlarge";
export type ButtonType = "cta" | "primary" | "secondary" | "tertiary" | "grey" | "light-grey" | "clear";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  // Core props
  size?: ButtonSize;
  buttonType?: ButtonType;
  block?: boolean;

  // Visual modifiers
  rounded?: boolean;
  bold?: boolean;
  underlined?: boolean;
  italic?: boolean;

  // Special button types
  fab?: boolean;
  menu?: boolean;
  toggle?: boolean;
  toggled?: boolean;

  // Icons
  leftIconName?: string;
  rightIconName?: string;
  centerIconName?: string; // For fab buttons primarily
  iconStyle?: string;

  // Content
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "default",
      buttonType = "primary",
      block = false,
      rounded = false,
      bold = false,
      underlined = false,
      italic = false,
      fab = false,
      menu = false,
      toggle = false,
      toggled = false,
      leftIconName,
      rightIconName,
      centerIconName,
      iconStyle = "outlined",
      disabled = false,
      className = "",
      children,
      ...props
    },
    ref,
  ) => {
    // Build class names
    const classes = [
      "button",
      `button--${buttonType}`,
      `button--${size}`,
      block && "button--block",
      rounded && "button--rounded",
      bold && "button--bold",
      underlined && "button--underlined",
      italic && "button--italic",
      fab && "button--fab",
      menu && "button--menu",
      toggle && "button--toggle",
      toggled && "button--toggle--toggled",
      disabled && "button--disabled",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const renderIcon = (iconName: string, position: "left" | "right" | "center") => {
      if (!iconName) {
        return null;
      }

      const parsedIconStyle = iconStyle === "filled" ? "-outlined filled-icon" : `-${iconStyle}`; //blank space for filled icon important

      const iconClass =
        position === "center"
          ? `material-symbols${parsedIconStyle}`
          : `material-symbols${parsedIconStyle} button__icon--${position}`;

      return <span className={iconClass}>{iconName}</span>;
    };

    return (
      <button ref={ref} className={classes} disabled={disabled} {...props}>
        {leftIconName && renderIcon(leftIconName, "left")}
        {centerIconName ? renderIcon(centerIconName, "center") : children}
        {rightIconName && renderIcon(rightIconName, "right")}
      </button>
    );
  },
);

Button.displayName = "Button";
